import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Modal } from './Modal';
import fetchWithAuth from '../../utils/fetchWithAuth';

const EolModal = ({
  isOpen,
  onClose,
  projectId,
  onSuccess,
  mode = 'add', // 'add', 'edit', 'remove'
  product = null // Produit à modifier/supprimer
}) => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [eolDate, setEolDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Gestion de l'état du modal
  useEffect(() => {
    if (isOpen) {
      // Charger les produits pour le mode add
      if (mode === 'add' && projectId) {
        loadAvailableProducts();
      }

      // Initialiser les valeurs selon le mode
      if (mode === 'edit' && product) {
        setSelectedProduct({ value: product.name, label: product.name });
        setEolDate(product.terminated_at ? format(new Date(product.terminated_at), 'yyyy-MM-dd') : '');
      } else {
        setSelectedProduct(null);
        setEolDate('');
      }
      setError('');
    } else {
      // Réinitialiser quand le modal se ferme
      setSelectedProduct(null);
      setEolDate('');
      setError('');
    }
  }, [isOpen, mode, product, projectId]);

  const loadAvailableProducts = async () => {
    setLoadingProducts(true);
    setError('');

    try {
      const data = await fetchWithAuth(`/api/globe-data/${projectId}/products-available`);
      setProducts(data.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Erreur lors du chargement des produits');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (mode !== 'remove' && (!selectedProduct || !eolDate)) {
      setError('Veuillez sélectionner un produit et une date de fin de vie');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const endpoint = mode === 'remove' ? `/api/globe-data/${projectId}/remove-eol-product` : `/api/globe-data/${projectId}/add-eol-product`;
      const body = mode === 'remove'
        ? { productName: product.name }
        : { productName: selectedProduct.value, terminatedAt: eolDate };

      await fetchWithAuth(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      console.log(`Product EOL ${mode} operation completed successfully`);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(`Error in EOL ${mode} operation:`, error);
      const errorMessages = {
        add: 'Erreur lors de l\'ajout du produit EOL',
        edit: 'Erreur lors de la modification du produit EOL',
        remove: 'Erreur lors de la suppression du produit EOL'
      };
      setError(errorMessages[mode] || 'Erreur lors de l\'opération sur le produit EOL');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    try {
      return format(new Date(date), 'yyyy-MM-dd');
    } catch (error) {
      return '';
    }
  };

  const getModalConfig = () => {
    const configs = {
      add: {
        title: 'Ajouter un Produit EOL',
        showProductSelect: true,
        showDateInput: true,
        showWarning: false
      },
      edit: {
        title: 'Modifier la Date de Fin de Vie',
        showProductSelect: false,
        showDateInput: true,
        showWarning: false
      },
      remove: {
        title: 'Retirer le Produit EOL',
        showProductSelect: false,
        showDateInput: false,
        showWarning: true
      }
    };
    return configs[mode] || configs.add;
  };

  const getButtonConfig = () => {
    const baseClass = "px-6 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200";

    const configs = {
      add: {
        text: 'Ajouter',
        loadingText: 'Ajout...',
        className: `${baseClass} bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 text-white`
      },
      edit: {
        text: 'Modifier',
        loadingText: 'Modification...',
        className: `${baseClass} bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 text-white`
      },
      remove: {
        text: 'Retirer',
        loadingText: 'Suppression...',
        className: `${baseClass} bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white`
      }
    };

    return configs[mode] || configs.add;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalConfig().title}
      size={mode === 'remove' ? 'xs' : 's'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Message d'avertissement pour suppression */}
        {getModalConfig().showWarning && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">
                  Retirer le produit de la liste EOL
                </h3>
                <div className="text-sm text-red-700 dark:text-red-300 space-y-1">
                  <p>
                    Le produit <strong className="font-medium">{product?.name}</strong> sera retiré de la liste des produits en fin de vie.
                  </p>

                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sélection du produit */}
        {getModalConfig().showProductSelect && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Produit
            </label>
            {loadingProducts ? (
              <div className="flex items-center justify-center p-4 border border-gray-300 dark:border-gray-600 rounded-md">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-400">Chargement des produits...</span>
              </div>
            ) : (
              <select
                value={selectedProduct?.value || ''}
                onChange={(e) => {
                  const product = products.find(p => p.value === e.target.value);
                  setSelectedProduct(product || null);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                required
              >
                <option value="">Sélectionner un produit</option>
                {products.map((product) => (
                  <option key={product.value} value={product.value}>
                    {product.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Affichage du produit (modes edit/remove) */}
        {!getModalConfig().showProductSelect && product && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Produit
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
              {product.name}
            </div>
          </div>
        )}

        {/* Date de fin de vie */}
        {getModalConfig().showDateInput && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date de Fin de Vie
            </label>
            <input
              type="date"
              value={eolDate}
              onChange={(e) => setEolDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
              required
              max={formatDateForInput(new Date())} // Ne pas permettre de dates futures
            />
          </div>
        )}

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Boutons d'action */}
        <div className={`flex justify-center space-x-4 ${mode === 'remove' ? 'pt-3' : 'pt-6'}`}>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
            disabled={loading}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading || (mode !== 'remove' && (!selectedProduct || !eolDate))}
            className={getButtonConfig().className}
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {getButtonConfig().loadingText}
              </div>
            ) : (
              getButtonConfig().text
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EolModal;
